import type { SchoolInvoiceRow } from './useSchoolInvoicesQuery';

// The Billing tab's rule (`voidDisabledReason` in pages/FamilyDetail/BillingTab.tsx),
// copied rather than shared: that one reads the checkout and auto-pay
// sub-statuses off a family invoice, which a school-wide row folds into its
// display status. Keep the two in step.
export function voidDisabledReason(invoice: Pick<SchoolInvoiceRow, 'status' | 'displayStatus'>) {
  if (invoice.status === 'PAYMENT_PROCESSING' || invoice.displayStatus === 'Payment Processing') {
    return 'Payment is processing; wait for Stripe settlement.';
  }
  if (invoice.status === 'PAID') return 'Paid invoices need refund or credit handling.';
  if (invoice.status === 'VOID') return 'Invoice is already voided.';
  if (invoice.status === 'DRAFT') return 'Delete draft invoices instead.';
  if (!['UNPAID', 'SCHEDULED', 'PAYMENT_FAILED'].includes(invoice.status)) return 'Invoice cannot be voided in this state.';
  return null;
}

// The Billing tab's rule (`deleteReason` in pages/FamilyDetail/BillingTab.tsx):
// only a draft can be deleted. Posted rows are voided instead.
export function deleteDisabledReason(invoice: Pick<SchoolInvoiceRow, 'status'>) {
  return invoice.status === 'DRAFT' ? null : 'Only draft invoices can be deleted.';
}

// A row the admin may tick: anything postable, plus every draft even when it
// cannot be posted (a subscription-managed draft), so it can still be edited
// or deleted in bulk.
export function selectable(invoice: Pick<SchoolInvoiceRow, 'status' | 'postable'>) {
  return invoice.postable || invoice.status === 'DRAFT';
}

// The description an invoice was created with: the memo text before " — ",
// the separator Billing::CreateInvoiceSet puts between the description and
// the installment or student label. The whole memo when there is none. This
// mirrors Billing::UpdateSchoolDrafts, which swaps exactly this substring.
export function currentDescription(invoice: Pick<SchoolInvoiceRow, 'memo'>): string {
  const memo = invoice.memo ?? '';
  const separatorAt = memo.indexOf(' — ');
  return (separatorAt === -1 ? memo : memo.slice(0, separatorAt)).trim();
}

// The description every given row shares, or null when they differ (or there
// are none); the Edit dialog prefills from this.
export function sharedDescription(invoices: readonly Pick<SchoolInvoiceRow, 'memo'>[]): string | null {
  if (invoices.length === 0) return null;
  const first = currentDescription(invoices[0]);
  if (first === '') return null;
  return invoices.every((invoice) => currentDescription(invoice) === first) ? first : null;
}

export function distinctDueDates(invoices: readonly Pick<SchoolInvoiceRow, 'dueDate'>[]): string[] {
  return Array.from(new Set(invoices.map((invoice) => invoice.dueDate ?? '')));
}

export type CollectionLabel = 'Auto-pay' | 'Sent to pay' | 'Not posted';

// How the row is collected once posted; a row with no Stripe invoice yet has
// not been posted at all.
export function collectionLabel(invoice: Pick<SchoolInvoiceRow, 'stripeInvoiceId' | 'stripeCollectionMethod'>): CollectionLabel {
  if (!invoice.stripeInvoiceId) return 'Not posted';
  return invoice.stripeCollectionMethod === 'charge_automatically' ? 'Auto-pay' : 'Sent to pay';
}

export function paymentMethodLabel(
  invoice: Pick<SchoolInvoiceRow, 'paymentMethodOnFile' | 'paymentMethodType' | 'paymentMethodLast4'>
): string {
  if (!invoice.paymentMethodOnFile) return 'None';
  const kind =
    invoice.paymentMethodType === 'card'
      ? 'Card'
      : invoice.paymentMethodType === 'us_bank_account'
        ? 'Bank account'
        : invoice.paymentMethodType
          ? invoice.paymentMethodType.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
          : 'On file';
  return invoice.paymentMethodLast4 ? `${kind} ····${invoice.paymentMethodLast4}` : kind;
}

// A row the table can search, sort, and facet on without re-deriving labels in
// every cell.
export type SchoolInvoiceTableRow = SchoolInvoiceRow & {
  searchText: string;
  collection: CollectionLabel;
  paymentMethod: string;
};

export function toTableRow(invoice: SchoolInvoiceRow): SchoolInvoiceTableRow {
  return {
    ...invoice,
    searchText: [invoice.familyName, invoice.memo ?? ''].join(' ').trim(),
    collection: collectionLabel(invoice),
    paymentMethod: paymentMethodLabel(invoice),
  };
}
